<template>
	<!-- Header del sitio: logo a la izquierda; moneda, idioma y la cuenta a la
	     derecha. La cuenta es un círculo con la foto o las iniciales que abre un
	     menú según el rol (huésped: Mi cuenta · anfitrión: + Mis espacios ·
	     admin: + Administrador · property: + calendario e informes). Reemplaza
	     al header anterior y a la barra de pestañas de abajo (NavMenu.vue). -->
	<div class="app-header" @keydown="alTeclear">
		<nav class="ah-nav" :aria-label="$t('nuevo_menu.hd.nav')">
			<div class="ah-inner">
				<router-link to="/" class="ah-logo" :aria-label="$t('nuevo_menu.hd.home')">
					<img class="ah-logo-full" :src="logoHorizontal" alt="" width="150" height="34">
					<img class="ah-logo-mark" :src="logoMark" alt="" width="34" height="34">
				</router-link>

				<!-- Contenido propio de una página, entre el logo y la derecha
				     (la portada pone acá Partners / Universidades / Anfitriones). -->
				<div v-if="$slots.default" class="ah-center"><slot /></div>

				<div class="ah-right">
					<!-- Moneda -->
					<div class="ah-dd">
						<button
							ref="btn-moneda"
							type="button"
							class="ah-pill"
							aria-haspopup="listbox"
							:aria-expanded="String(abierto === 'moneda')"
							aria-controls="ah-dd-moneda"
							:aria-label="`${$t('nuevo_menu.hd.currency')}: ${visitorCurrency || 'USD'}`"
							@click.stop="alternar('moneda')"
						>
							<span class="ah-pill-ico" aria-hidden="true">$</span>
							<span class="ah-pill-txt">{{ visitorCurrency || 'USD' }}</span>
							<span class="ah-chev" aria-hidden="true" v-html="ICON.chevDown"></span>
						</button>
						<ul v-show="abierto === 'moneda'" id="ah-dd-moneda" ref="pop-moneda" class="ah-pop ah-pop--list" role="listbox" :aria-label="$t('nuevo_menu.hd.currency')" @click.stop>
							<li v-for="c in currencyConversionTable" :key="c.moneda">
								<button type="button" class="ah-opt" role="option" :aria-selected="String(visitorCurrency === c.moneda)" @click="setCurrency(c.moneda)">
									<span class="ah-opt-code">{{ c.moneda }}</span>
								</button>
							</li>
						</ul>
					</div>

					<!-- Idioma -->
					<div class="ah-dd">
						<button
							ref="btn-idioma"
							type="button"
							class="ah-pill"
							aria-haspopup="listbox"
							:aria-expanded="String(abierto === 'idioma')"
							aria-controls="ah-dd-idioma"
							:aria-label="`${$t('nuevo_menu.hd.language')}: ${idiomaActual.nombre}`"
							@click.stop="alternar('idioma')"
						>
							<span class="ah-pill-ico" aria-hidden="true" v-html="ICON.globe"></span>
							<span class="ah-pill-txt">{{ idiomaActual.text }}</span>
							<span class="ah-chev" aria-hidden="true" v-html="ICON.chevDown"></span>
						</button>
						<ul v-show="abierto === 'idioma'" id="ah-dd-idioma" ref="pop-idioma" class="ah-pop ah-pop--list" role="listbox" :aria-label="$t('nuevo_menu.hd.language')" @click.stop>
							<li v-for="lang in langs" :key="lang.code">
								<button type="button" class="ah-opt" role="option" :lang="lang.code" :aria-selected="String(selectedLang === lang.code)" @click="changeLang(lang.code)">
									<span class="ah-opt-code">{{ lang.text }}</span>
									<span class="ah-opt-name">{{ lang.nombre }}</span>
								</button>
							</li>
						</ul>
					</div>

					<!-- Sin sesión -->
					<template v-if="!isLoggedIn">
						<router-link to="/login" class="ah-btn ah-btn--ghost">{{ $t('main-menu.sign-in') }}</router-link>
						<router-link :to="{ name: 'register', params: { step: 1 } }" class="ah-btn ah-btn--primary ah-signup">{{ $t('main-menu.sign-up') }}</router-link>
					</template>

					<!-- Con sesión: notificaciones -->
					<NotificacionesCampana v-if="isLoggedIn" ref="campana" @abrir="cerrar()" />

					<!-- Con sesión: la cuenta -->
					<div v-if="isLoggedIn" class="ah-dd ah-account">
						<button
							ref="btn-cuenta"
							type="button"
							class="ah-av-btn"
							aria-haspopup="menu"
							:aria-expanded="String(abierto === 'cuenta')"
							aria-controls="ah-dd-cuenta"
							:aria-label="$t('nuevo_menu.hd.account')"
							@click.stop="alternar('cuenta')"
						>
							<span class="ah-av" aria-hidden="true">
								<img v-if="fotoUrl" :src="fotoUrl" alt="" @error="fotoRota = true">
								<template v-else>{{ iniciales }}</template>
							</span>
							<span class="ah-av-badge" aria-hidden="true" v-html="ICON.chevDown"></span>
						</button>

						<div v-show="abierto === 'cuenta'" id="ah-dd-cuenta" ref="pop-cuenta" class="ah-pop ah-am" role="menu" aria-labelledby="ah-am-name" @click.stop>
							<div class="ah-am-head">
								<span class="ah-av ah-av--lg" aria-hidden="true">
									<img v-if="fotoUrl" :src="fotoUrl" alt="">
									<template v-else>{{ iniciales }}</template>
								</span>
								<div class="ah-am-who">
									<p id="ah-am-name" class="ah-am-name">{{ nombreCompleto }}</p>
									<p class="ah-am-mail">{{ correo }}</p>
									<span :class="['ah-am-role', `ah-am-role--${rol}`]">{{ $t(`nuevo_menu.rol.${rol}`) }}</span>
								</div>
							</div>

							<router-link
								v-for="item in itemsMenu"
								:key="item.to"
								:to="item.to"
								:class="['ah-am-item', { 'is-current': item.actual }]"
								role="menuitem"
								:aria-current="item.actual ? 'true' : null"
								@click.native="cerrar()"
							>
								<span class="ah-am-ico" v-html="item.icono"></span>
								<span class="ah-am-txt">
									<span class="ah-am-title">{{ $t(item.titulo) }}</span>
									<span class="ah-am-desc">{{ $t(item.desc) }}</span>
								</span>
							</router-link>

							<div class="ah-am-sep" role="separator"></div>
							<router-link :to="{ name: 'conversation' }" class="ah-am-item ah-am-item--simple" role="menuitem" @click.native="cerrar()">
								<span class="ah-am-ico" v-html="ICON.msg"></span>
								<span class="ah-am-title">{{ $t('nuevo_menu.menu.messages') }}</span>
							</router-link>
							<button type="button" class="ah-am-item ah-am-item--simple ah-am-item--out" role="menuitem" @click="handleLogout">
								<span class="ah-am-ico" v-html="ICON.out"></span>
								<span class="ah-am-title">{{ $t('nuevo_menu.menu.sign_out') }}</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import axios from 'axios';
// Logo naranja de marca (#FC7010).
import logoHorizontal from '@/new-assets/logo-horizontal.svg';
import logoMark from '@/assets/hyf-logo-mark-orange.png';
import ICON from '@/components/Cuenta/iconos';
import NotificacionesCampana from '@/components/Notificaciones/NotificacionesCampana.vue';

export default {
	name: 'AppHeader',
	components: { NotificacionesCampana },
	data() {
		return {
			logoHorizontal,
			logoMark,
			ICON,
			langs: [
				{ code: 'es', text: 'ESP', nombre: 'Español' },
				{ code: 'en', text: 'ENG', nombre: 'English' },
				{ code: 'fr', text: 'FRA', nombre: 'Français' },
				{ code: 'it', text: 'ITA', nombre: 'Italiano' },
				{ code: 'de', text: 'DEU', nombre: 'Deutsch' },
				{ code: 'pt', text: 'POR', nombre: 'Português' }
			],
			selectedLang: '',
			// moneda | idioma | cuenta | null
			abierto: null,
			fotoRota: false,
			s3Url: process.env.VUE_APP_S3_URL
		};
	},
	computed: {
		...mapState('exchange', ['currencyConversionTable', 'visitorCurrency']),
		...mapState(['user', 'esAnfitrion']),
		...mapGetters(['isLoggedIn', 'isAdmin', 'isProperty', 'isHost']),
		idiomaActual() {
			return this.langs.find(l => l.code === this.selectedLang) || this.langs[0];
		},
		perfil() {
			try { return JSON.parse(localStorage.getItem('usuario') || '{}') || {}; } catch (e) { return {}; }
		},
		nombre() { return (this.user && this.user.nombre) || this.perfil.nombre || ''; },
		apellido() { return (this.user && this.user.apellido) || this.perfil.apellido || ''; },
		correo() { return (this.user && this.user.correo) || this.perfil.correo || ''; },
		nombreCompleto() {
			return [this.nombre, this.apellido].filter(v => v && v !== 'null').join(' ') || this.correo;
		},
		iniciales() {
			const ini = [this.nombre, this.apellido].filter(v => v && v !== 'null').map(v => v.trim().charAt(0)).join('');
			return (ini || (this.correo || 'U').charAt(0)).toUpperCase();
		},
		fotoUrl() {
			const img = this.user && this.user.imagen;
			if (this.fotoRota || !img || img === 'null' || img === 'undefined') return null;
			return /^https?:/.test(img) ? img : `${this.s3Url}perfil/${img}`;
		},
		rol() {
			if (this.isAdmin) return 'admin';
			if (this.isProperty) return 'property';
			return this.isHost ? 'anfitrion' : 'huesped';
		},
		seccion() {
			const p = this.$route.path;
			if (['/editar-perfil', '/cambiar-password', '/datos-bancarios', '/reservations-made', '/reservations-received'].includes(p)) return 'cuenta';
			if (['/my-accommodations', '/icalendar'].includes(p)) return 'espacios';
			if (p === '/admin') return 'admin';
			return p;
		},
		itemsMenu() {
			const items = [{ to: '/editar-perfil', icono: ICON.user, titulo: 'nuevo_menu.menu.my_account', desc: this.isProperty ? 'nuevo_menu.menu.my_account_desc_short' : 'nuevo_menu.menu.my_account_desc', sec: 'cuenta' }];
			if (this.isHost) items.push({ to: '/my-accommodations', icono: ICON.home, titulo: 'nuevo_menu.menu.my_spaces', desc: 'nuevo_menu.menu.my_spaces_desc', sec: 'espacios' });
			// El usuario "property" tenía Ver calendario e Informes en el menú anterior.
			if (this.isProperty) {
				items.push({ to: '/calendario', icono: ICON.cal, titulo: 'nuevo_menu.menu.calendar', desc: 'nuevo_menu.menu.calendar_desc', sec: '/calendario' });
				items.push({ to: '/informes', icono: ICON.report, titulo: 'nuevo_menu.menu.reports', desc: 'nuevo_menu.menu.reports_desc', sec: '/informes' });
			}
			if (this.isAdmin) items.push({ to: '/admin', icono: ICON.admin, titulo: 'nuevo_menu.menu.admin', desc: 'nuevo_menu.menu.admin_desc', sec: 'admin' });
			return items.map(i => ({ ...i, actual: i.sec === this.seccion }));
		}
	},
	methods: {
		...mapActions('exchange', ['setVisitorCurrency', 'fetchCurrencyConversionTable']),
		...mapActions(['logout']),
		changeLang(langCode) {
			this.selectedLang = langCode;
			this.$i18n.locale = langCode;
			this.$store.state.iso639_1 = langCode;
			localStorage.setItem('iso639_1', langCode);
			if (this.isLoggedIn) {
				this.$store.dispatch('changePreferLanguage', { language: langCode });
			}
			this.cerrar(true);
		},
		setCurrency(currency) {
			this.setVisitorCurrency(currency);
			localStorage.setItem('visitorCurrency', currency);
			this.cerrar(true);
		},
		handleLogout() {
			this.cerrar();
			this.logout();
			this.$router.push('/');
		},

		// ── Desplegables: uno abierto a la vez, Escape / clic afuera cierran,
		//    flechas recorren las opciones. ──
		alternar(cual) {
			if (this.abierto === cual) return this.cerrar(true);
			if (this.$refs.campana) this.$refs.campana.cerrar();
			this.abierto = cual;
			this.$nextTick(() => {
				const items = this.opciones();
				const actual = items.find(i => i.getAttribute('aria-selected') === 'true' || i.classList.contains('is-current')) || items[0];
				if (actual) actual.focus();
			});
		},
		cerrar(devolverFoco = false) {
			const cual = this.abierto;
			this.abierto = null;
			if (devolverFoco && cual && this.$refs[`btn-${cual}`]) this.$refs[`btn-${cual}`].focus();
		},
		opciones() {
			const pop = this.abierto && this.$refs[`pop-${this.abierto}`];
			return pop ? [...pop.querySelectorAll('.ah-opt, .ah-am-item')] : [];
		},
		alTeclear(e) {
			if (!this.abierto) return;
			const items = this.opciones();
			const i = items.indexOf(document.activeElement);
			if (e.key === 'Escape') { e.preventDefault(); this.cerrar(true); }
			else if (e.key === 'ArrowDown' && items.length) { e.preventDefault(); items[(i + 1) % items.length].focus(); }
			else if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); items[(i - 1 + items.length) % items.length].focus(); }
			else if (e.key === 'Home' && items.length) { e.preventDefault(); items[0].focus(); }
			else if (e.key === 'End' && items.length) { e.preventDefault(); items[items.length - 1].focus(); }
			else if (e.key === 'Tab') this.cerrar();
		},
		alClicFuera() {
			if (this.abierto) this.cerrar();
		},

		// Muchos anfitriones no tienen `tipo`: se averigua una vez por sesión si
		// tiene alojamientos (decide si ve "Mis espacios" y las reservas recibidas).
		async averiguarAnfitrion() {
			if (!this.isLoggedIn || this.esAnfitrion != null) return;
			if (this.isAdmin || this.isProperty || (this.user && this.user.tipo === 'anfitrion')) return;
			try {
				const { data } = await axios.get(`${process.env.VUE_APP_API_URL}accommodations/get/accommodations`);
				const total = data && data.resumen ? data.resumen.total : (data && data.total) || 0;
				if (this.isLoggedIn) this.$store.commit('setEsAnfitrion', total > 0);
			} catch (e) {
				// Sin respuesta: se vuelve a intentar en la próxima carga.
			}
		}
	},
	created() {
		this.fetchCurrencyConversionTable().then(() => {
			let localStorageVisitorCurrency = localStorage.getItem('visitorCurrency');
			if (!localStorageVisitorCurrency) {
				localStorageVisitorCurrency = 'USD';
				localStorage.setItem('visitorCurrency', localStorageVisitorCurrency);
			}
			this.setVisitorCurrency(localStorageVisitorCurrency);
		});

		const storedLang = localStorage.getItem('iso639_1');
		this.selectedLang = storedLang ? storedLang : this.$i18n.locale;
		this.averiguarAnfitrion();
	},
	mounted() {
		document.addEventListener('click', this.alClicFuera);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.alClicFuera);
	},
	watch: {
		$route() {
			this.cerrar();
		},
		isLoggedIn(v) {
			if (v) this.$nextTick(this.averiguarAnfitrion);
		},
		'user.imagen'() {
			this.fotoRota = false;
		}
	}
};
</script>

<style lang="scss" scoped>
.app-header {
	--ah-orange: #ff7a00;
	--ah-orange-dark: #e8640a;
	--ah-magenta: #B91E7C;
	--ah-violet: #6d3ce0;
	--ah-ink: #1d1a17;
	--ah-ink-2: #3b3530;
	--ah-muted: #6f6760;
	--ah-line: #ece4dc;
	--ah-line-2: #ddd2c7;
	--ah-danger: #d93a2b;
	--ah-ring: 0 0 0 4px rgba(255, 122, 0, .16);
	--ah-grad: linear-gradient(90deg, #ff8414, #f26b05);
	--ah-grad-d: linear-gradient(180deg, #ff8414, #f26b05);
	--ah-ease: cubic-bezier(.22, 1, .36, 1);

	/* El fondo y el desenfoque los pinta .ah-inner: un backdrop-filter acá
	   haría de este contenedor el bloque contenedor de los position: fixed. */
	position: sticky;
	top: 0;
	z-index: 999;
	font-family: 'DM Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
	color: var(--ah-ink);
	-webkit-font-smoothing: antialiased;

	:focus-visible { outline: 2px solid var(--ah-orange); outline-offset: 2px; }
	ul, p { margin: 0; padding: 0; list-style: none; }
	a, a:hover { text-decoration: none; }
	button { font: inherit; }
	button:focus { outline: none; }
	button:focus-visible { outline: 2px solid var(--ah-orange); outline-offset: 2px; }
}

.ah-nav { position: relative; }
/* Fondo del header de punta a punta. Va en .ah-nav (left/right 0) y no con
   width: 100vw: en Windows 100vw incluye la barra de scroll y el header
   quedaba ~8 px más ancho que la página → scroll horizontal. Como pseudo-
   elemento, su backdrop-filter no vuelve a .ah-nav bloque contenedor de los
   position: fixed (el menú de la cuenta en celular). */
.ah-nav::before {
	content: '';
	position: absolute;
	top: 0; bottom: 0; left: 0; right: 0;
	z-index: 1;
	/* Línea abajo + sombra suave para que el header se despegue del fondo
	   de marca (mismo estilo que el de login/registro, Registration/Layout.vue). */
	background: rgba(255, 255, 255, .94);
	-webkit-backdrop-filter: blur(14px) saturate(1.4);
	backdrop-filter: blur(14px) saturate(1.4);
	border-bottom: 1px solid rgba(29, 26, 23, .12);
	box-shadow: 0 8px 24px -14px rgba(29, 26, 23, .28);
}

/* De punta a punta: el logo siempre contra la izquierda y el resto contra
   la derecha (sin max-width que los centre en pantallas anchas). */
.ah-inner {
	position: relative;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	height: 72px;
	padding: 0 32px;
}

.ah-logo { display: flex; align-items: center; flex: none; border-radius: 8px; }
.ah-logo-full { width: 150px; height: auto; }
.ah-logo-mark { display: none; width: 34px; height: 34px; }
.ah-right { display: flex; align-items: center; gap: 8px; }
.ah-center { display: flex; align-items: center; justify-content: center; gap: 28px; flex: 1 1 auto; min-width: 0; }

/* Píldoras de moneda / idioma */
.ah-pill {
	display: inline-flex; align-items: center; gap: 7px;
	height: 42px; padding: 0 12px 0 14px; margin: 0;
	border: 1.5px solid var(--ah-line-2); border-radius: 999px; background: #fff;
	font-size: 14px; font-weight: 600; color: var(--ah-ink); white-space: nowrap; cursor: pointer;
	transition: border-color .2s, box-shadow .2s;
	&:hover { border-color: #cbbcae; }
	&[aria-expanded="true"] { border-color: var(--ah-orange); box-shadow: var(--ah-ring); }
}
.ah-pill-ico { display: grid; place-items: center; width: 17px; height: 17px; font-weight: 700; color: var(--ah-orange-dark); }
.ah-pill-ico ::v-deep svg { width: 17px; height: 17px; }
.ah-pill-txt { font-variant-numeric: tabular-nums; letter-spacing: .02em; }
.ah-chev { display: grid; color: var(--ah-muted); transition: transform .25s var(--ah-ease); }
.ah-chev ::v-deep svg { width: 14px; height: 14px; }
[aria-expanded="true"] > .ah-chev { transform: rotate(180deg); }

/* Desplegables */
.ah-dd { position: relative; }
.ah-pop {
	position: absolute; right: 0; top: calc(100% + 10px); z-index: 60;
	min-width: 190px; padding: 6px;
	border-radius: 16px; background: #fff;
	border: 1px solid rgba(29, 26, 23, .08);
	box-shadow: 0 24px 60px -18px rgba(29, 26, 23, .32), 0 4px 14px -6px rgba(29, 26, 23, .12);
	transform-origin: top right;
	animation: ah-pop .22s var(--ah-ease) both;
}
.ah-pop--list { max-height: min(60vh, 360px); overflow-y: auto; }
.ah-opt {
	display: flex; align-items: center; gap: 10px; width: 100%;
	padding: 10px 12px; margin: 0; border: 0; border-radius: 10px; background: none;
	font-size: 14px; text-align: left; color: var(--ah-ink); cursor: pointer;
	&:hover, &:focus-visible { background: #fff7ef; outline: none; }
	&[aria-selected="true"] { color: var(--ah-orange-dark); font-weight: 700; }
	&[aria-selected="true"]::after {
		content: ''; width: 6px; height: 11px; margin: -3px 2px 0 auto;
		border: solid currentColor; border-width: 0 2px 2px 0; transform: rotate(45deg);
	}
}
.ah-opt-code { min-width: 38px; font-weight: 700; font-variant-numeric: tabular-nums; }
.ah-opt-name { color: var(--ah-muted); }
.ah-opt[aria-selected="true"] .ah-opt-name { color: inherit; }

/* Sin sesión */
.ah-btn {
	display: inline-flex; align-items: center; justify-content: center;
	min-height: 42px; padding: 0 18px;
	border: 1.5px solid transparent; border-radius: 999px;
	font-size: 14px; font-weight: 700; white-space: nowrap;
	transition: transform .15s, box-shadow .2s, border-color .2s;
}
.ah-btn--ghost { color: var(--ah-ink) !important; background: #fff; border-color: var(--ah-line-2); &:hover { border-color: var(--ah-ink); } }
.ah-btn--primary { color: #fff !important; background: linear-gradient(180deg, #ff8414, #f26b05); box-shadow: 0 10px 22px -12px rgba(232, 100, 10, .75); &:hover { transform: translateY(-1px); } }

/* Avatar */
.ah-av-btn {
	position: relative; display: grid; place-items: center;
	width: 46px; height: 46px; padding: 0; margin: 0 0 0 4px;
	border: 0; border-radius: 50%; background: none; cursor: pointer;
}
.ah-av {
	display: grid; place-items: center; flex: none;
	width: 42px; height: 42px; border-radius: 50%; overflow: hidden;
	background: var(--ah-grad-d); color: #fff;
	font-size: 15px; font-weight: 700; letter-spacing: -.01em;
	box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px var(--ah-line-2);
	transition: box-shadow .2s;
	img { width: 100%; height: 100%; object-fit: cover; }
}
.ah-av-btn:hover .ah-av { box-shadow: 0 0 0 2px #fff, 0 0 0 3.5px #cbbcae; }
.ah-av-btn[aria-expanded="true"] .ah-av { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ah-orange), 0 0 0 9px rgba(255, 122, 0, .14); }
.ah-av-badge {
	position: absolute; right: 0; bottom: 0;
	display: grid; place-items: center; width: 18px; height: 18px; border-radius: 50%;
	background: #fff; color: var(--ah-ink); box-shadow: 0 2px 6px rgba(29, 26, 23, .2);
	::v-deep svg { width: 11px; height: 11px; transition: transform .25s var(--ah-ease); }
}
.ah-av-btn[aria-expanded="true"] .ah-av-badge ::v-deep svg { transform: rotate(180deg); }

/* Menú de la cuenta */
.ah-am { width: 320px; padding: 8px; }
.ah-am-head { display: flex; align-items: center; gap: 12px; padding: 10px 10px 14px; margin-bottom: 6px; border-bottom: 1px solid var(--ah-line); }
.ah-av--lg { width: 46px; height: 46px; font-size: 16px; box-shadow: none; }
.ah-am-who { min-width: 0; }
.ah-am-name { font-size: 15px; font-weight: 700; line-height: 1.3; color: var(--ah-ink); word-break: break-word; }
.ah-am-mail { font-size: 13px; color: var(--ah-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ah-am-role {
	display: inline-flex; margin-top: 5px; padding: 2px 9px; border-radius: 999px;
	font-size: 11.5px; font-weight: 700; letter-spacing: .02em;
	background: #fff7ef; color: var(--ah-orange-dark);
}
.ah-am-role--admin { background: #f1ecff; color: var(--ah-violet); }
.ah-am-role--huesped { background: #eef6ff; color: #2b5fb4; }
.ah-am-item {
	display: flex; align-items: center; gap: 12px; width: 100%;
	padding: 10px; margin: 0; border: 0; border-radius: 12px; background: none;
	text-align: left; color: var(--ah-ink) !important; cursor: pointer;
	transition: background .15s;
	&:hover, &:focus-visible { background: #fff7ef; outline: none; }
	&:focus-visible { box-shadow: inset 0 0 0 2px var(--ah-orange); }
}
.ah-am-ico {
	display: grid; place-items: center; flex: none; width: 38px; height: 38px; border-radius: 12px;
	background: #f6f2ee; color: var(--ah-ink-2);
	::v-deep svg { width: 20px; height: 20px; }
}
.ah-am-item:hover .ah-am-ico { background: #fff; color: var(--ah-orange-dark); }
.ah-am-item.is-current .ah-am-ico { background: var(--ah-grad-d); color: #fff; }
.ah-am-txt { display: flex; flex-direction: column; min-width: 0; }
.ah-am-title { font-size: 15px; font-weight: 600; line-height: 1.3; }
.ah-am-desc { font-size: 12.5px; line-height: 1.35; color: var(--ah-muted); }
.ah-am-sep { height: 1px; margin: 6px 4px; background: var(--ah-line); }
.ah-am-item--simple { padding: 9px 10px; }
.ah-am-item--simple .ah-am-ico { width: 30px; height: 30px; border-radius: 9px; background: none; }
.ah-am-item--out { color: var(--ah-danger) !important; }
.ah-am-item--out:hover { background: #fdeeec; }
.ah-am-item--out:hover .ah-am-ico { background: none; color: var(--ah-danger); }

@keyframes ah-pop { from { opacity: 0; transform: translateY(-6px) scale(.97); } to { opacity: 1; transform: none; } }

@media (max-width: 1200px) {
	.ah-inner { padding: 0 24px; }
}
@media (max-width: 760px) {
	.ah-inner { height: 62px; padding: 0 14px; gap: 8px; }
	/* Logo completo también en celular (antes solo el isotipo H&F): se
	   achica según el lugar que deja la derecha, entre 82 y 120 px. */
	.ah-logo { flex: 1 1 auto; min-width: 0; }
	.ah-logo-full { display: block; width: 100%; max-width: 120px; min-width: 82px; }
	.ah-logo-mark { display: none; }
	.ah-right { gap: 6px; flex: none; }
	.ah-pill { height: 40px; padding: 0 10px; gap: 5px; font-size: 13px; }
	/* Sin ícono ni flecha: las píldoras quedan solo con "USD" / "ESP". */
	.ah-pill-ico, .ah-chev { display: none; }
	.ah-btn { min-height: 40px; padding: 0 14px; font-size: 13.5px; }
	/* Sin sesión: un solo botón para que entre el logo; la pantalla de
	   login ya ofrece "Registrarse". */
	.ah-signup { display: none; }
	.ah-av-btn { width: 44px; height: 44px; }
	.ah-av { width: 38px; height: 38px; font-size: 14px; }
	/* El menú de la cuenta ocupa el ancho de la pantalla */
	.ah-account .ah-pop { position: fixed; left: 10px; right: 10px; top: 68px; width: auto; }
	.ah-dd .ah-pop--list { right: auto; left: 50%; translate: -50% 0; transform-origin: top center; }
}
@media (max-width: 380px) {
	.ah-inner { gap: 6px; padding: 0 12px; }
	.ah-right { gap: 5px; }
	.ah-logo-full { min-width: 76px; }
	.ah-pill { padding: 0 8px; }
	.ah-btn { padding: 0 11px; font-size: 13px; }
	.ah-signup { display: none; }
	.ah-pill-ico { display: none; }
	.ah-pill { padding: 0 8px; }
}
@media (prefers-reduced-motion: reduce) {
	.ah-pop { animation: none; }
}
</style>
